import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaArrowUp,
  FaArrowDown,
  FaUpload
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ServiceHeroExtrasEditor from '../../components/cms/ServiceHeroExtrasEditor';
import WhyChooseSectionEditor from '../../components/cms/WhyChooseSectionEditor';

const USACompanyFormationCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    heroTitle: '',
    heroSubtitle: '',
    heroBody: '',
    heroImage: '',
    heroBtn1Text: '',
    heroBtn1Path: '',
    heroBtn2Text: '',

    heroBadgeText: '',
    heroBadgeIcon: 'check',
    heroTrustBadges: [],

    servicesTitle: '',
    servicesSubtitle: '',
    services: [],

    whyChooseTitle: '',
    whyChooseBody: '',
    whyChooseImage: '',
    whyChooseTag: '',
    whyChooseImageBadgeLabel: '',
    whyChooseImageBadgeText: '',
    whyChooseImageBadgeIcon: 'shield',
    whyChooseFeatures: [],

    trackTitle: '',
    trackBody: '',
    trackBullets: [],
    trackButtonText: '',
    trackImage: '',

    guideTitle: '',
    guideSubtitle: '',
    guideBody: '',
    guideBoxNumber: '',
    guideBoxTitle: '',
    guideBoxSubtitle: '',
    guideSteps: [],

    faqsTitle: '',
    faqsSubtitle: '',
    faqs: [],

    isPublished: true
  });

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/usa-formation/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data || {};
        setForm({
          heroTitle: data.heroTitle || '',
          heroSubtitle: data.heroSubtitle || '',
          heroBody: data.heroBody || '',
          heroImage: data.heroImage || '',
          heroBtn1Text: data.heroBtn1Text || '',
          heroBtn1Path: data.heroBtn1Path || '',
          heroBtn2Text: data.heroBtn2Text || '',

          heroBadgeText: data.heroBadgeText || '',
          heroBadgeIcon: data.heroBadgeIcon || 'check',
          heroTrustBadges: data.heroTrustBadges || [],

          servicesTitle: data.servicesTitle || '',
          servicesSubtitle: data.servicesSubtitle || '',
          services: data.services || [],

          whyChooseTitle: data.whyChooseTitle || '',
          whyChooseBody: data.whyChooseBody || '',
          whyChooseImage: data.whyChooseImage || '',
          whyChooseTag: data.whyChooseTag || '',
          whyChooseImageBadgeLabel: data.whyChooseImageBadgeLabel || '',
          whyChooseImageBadgeText: data.whyChooseImageBadgeText || '',
          whyChooseImageBadgeIcon: data.whyChooseImageBadgeIcon || 'shield',
          whyChooseFeatures: data.whyChooseFeatures || [],

          trackTitle: data.trackTitle || '',
          trackBody: data.trackBody || '',
          trackBullets: data.trackBullets || [],
          trackButtonText: data.trackButtonText || '',
          trackImage: data.trackImage || '',

          guideTitle: data.guideTitle || '',
          guideSubtitle: data.guideSubtitle || '',
          guideBody: data.guideBody || '',
          guideBoxNumber: data.guideBoxNumber || '',
          guideBoxTitle: data.guideBoxTitle || '',
          guideBoxSubtitle: data.guideBoxSubtitle || '',
          guideSteps: data.guideSteps || [],

          faqsTitle: data.faqsTitle || '',
          faqsSubtitle: data.faqsSubtitle || '',
          faqs: data.faqs || [],

          isPublished: data.isPublished !== false
        });
      }
    } catch (err) {
      toastError('Failed to load USA Formation content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/usa-formation/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('USA Company Formation page content saved successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to revert USA Formation page options to default?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/usa-formation/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Restored default content successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/usa-formation/admin/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        updateField(field, res.data.url);
        toastSuccess('Image uploaded and optimized successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload image');
    }
  };

  // Helper arrays update methods
  const addService = () => {
    setForm(prev => ({
      ...prev,
      services: [...prev.services, { title: '', description: '', icon: '🏢', color: 'from-blue-500 to-blue-600' }]
    }));
  };

  const removeService = (idx) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== idx)
    }));
  };

  const updateServiceField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.services];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, services: list };
    });
  };

  const addFAQ = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const removeFAQ = (idx) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== idx)
    }));
  };

  const updateFAQField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.faqs];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, faqs: list };
    });
  };

  const addTrackBullet = () => {
    setForm(prev => ({
      ...prev,
      trackBullets: [...prev.trackBullets, '']
    }));
  };

  const removeTrackBullet = (idx) => {
    setForm(prev => ({
      ...prev,
      trackBullets: prev.trackBullets.filter((_, i) => i !== idx)
    }));
  };

  const updateTrackBulletField = (idx, val) => {
    setForm(prev => {
      const list = [...prev.trackBullets];
      list[idx] = val;
      return { ...prev, trackBullets: list };
    });
  };

  const addGuideStep = () => {
    setForm(prev => ({
      ...prev,
      guideSteps: [...prev.guideSteps, '']
    }));
  };

  const removeGuideStep = (idx) => {
    setForm(prev => ({
      ...prev,
      guideSteps: prev.guideSteps.filter((_, i) => i !== idx)
    }));
  };

  const updateGuideStepField = (idx, val) => {
    setForm(prev => {
      const list = [...prev.guideSteps];
      list[idx] = val;
      return { ...prev, guideSteps: list };
    });
  };

  const moveGuideStep = (idx, dir) => {
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= form.guideSteps.length) return;
    setForm(prev => {
      const list = [...prev.guideSteps];
      [list[idx], list[nextIdx]] = [list[nextIdx], list[idx]];
      return { ...prev, guideSteps: list };
    });
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="USA Company Formation CMS"
        description="Edit the public USA company setup, benefits, step-by-step formation guides, images, and FAQs page."
        icon={FaGlobe}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleRestoreDefaults}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
            >
              <FaUndo className="w-3.5 h-3.5" /> Revert Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto gap-2 mb-6">
        {[
          { id: 'hero', name: 'Hero Banner' },
          { id: 'services', name: 'Setup Services' },
          { id: 'benefits', name: 'Benefits & Tracking' },
          { id: 'guide', name: 'Formation Guide' },
          { id: 'faqs', name: 'Page FAQs' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 font-bold text-sm border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {/* Tab 1: Hero Banner */}
        {activeTab === 'hero' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hero Header Layout</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => updateField('isPublished', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Main Title</label>
                <input
                  className="input-modern"
                  value={form.heroTitle}
                  onChange={(e) => updateField('heroTitle', e.target.value)}
                  placeholder="USA Company Formation Services"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Subtitle</label>
                <input
                  className="input-modern"
                  value={form.heroSubtitle}
                  onChange={(e) => updateField('heroSubtitle', e.target.value)}
                  placeholder="made simple and fast"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Description Body</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.heroBody}
                onChange={(e) => updateField('heroBody', e.target.value)}
                placeholder="Brief summary sentence..."
              />
            </div>

            {/* Buttons Customization */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 border-t border-b border-gray-100 dark:border-dark-700 py-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn1Text}
                  onChange={(e) => updateField('heroBtn1Text', e.target.value)}
                  placeholder="See Plans & Pricing"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Link / Path</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn1Path}
                  onChange={(e) => updateField('heroBtn1Path', e.target.value)}
                  placeholder="/pricing"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Secondary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn2Text}
                  onChange={(e) => updateField('heroBtn2Text', e.target.value)}
                  placeholder="Book Free Consultation"
                />
              </div>
            </div>

            <ServiceHeroExtrasEditor form={form} updateField={updateField} />

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Side Image</label>
              <div className="flex items-center gap-4">
                <input
                  className="input-modern flex-1"
                  value={form.heroImage}
                  onChange={(e) => updateField('heroImage', e.target.value)}
                  placeholder="Image URL path..."
                />
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                  <FaUpload className="w-3.5 h-3.5" /> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'heroImage')}
                    className="hidden"
                  />
                </label>
              </div>
              {form.heroImage && (
                <div className="mt-2 relative w-48 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={form.heroImage.startsWith('http') ? form.heroImage : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.heroImage}`}
                    alt="Hero preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Setup Services */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Complete Setup Services</h2>
              <button
                onClick={addService}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Service Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.servicesTitle}
                  onChange={(e) => updateField('servicesTitle', e.target.value)}
                  placeholder="Complete USA Company Setup Services"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.servicesSubtitle}
                  onChange={(e) => updateField('servicesSubtitle', e.target.value)}
                  placeholder="Comprehensive solutions..."
                />
              </div>
            </div>

            {/* List of Services */}
            <div className="space-y-4 pt-4">
              {form.services.map((service, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeService(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Service Card #{idx + 1}</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.title}
                        onChange={(e) => updateServiceField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Emoji Icon</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.icon}
                        onChange={(e) => updateServiceField(idx, 'icon', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Background Gradient Class</label>
                      <select
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.color}
                        onChange={(e) => updateServiceField(idx, 'color', e.target.value)}
                      >
                        <option value="from-blue-500 to-blue-600">Blue Gradient</option>
                        <option value="from-orange-500 to-orange-600">Orange Gradient</option>
                        <option value="from-blue-600 to-blue-700">Dark Blue Gradient</option>
                        <option value="from-emerald-500 to-emerald-600">Emerald Gradient</option>
                        <option value="from-purple-500 to-purple-600">Purple Gradient</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                    <textarea
                      className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                      value={service.description}
                      onChange={(e) => updateServiceField(idx, 'description', e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: Benefits & Tracking */}
        {activeTab === 'benefits' && (
          <div className="space-y-6">
            {/* Benefits section */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">USA Company Benefits</h3>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                  <input
                    className="input-modern"
                    value={form.whyChooseTitle}
                    onChange={(e) => updateField('whyChooseTitle', e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Text Body</label>
                  <textarea
                    className="input-modern min-h-[100px]"
                    value={form.whyChooseBody}
                    onChange={(e) => updateField('whyChooseBody', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Benefits Side Image</label>
                  <div className="flex items-center gap-4">
                    <input
                      className="input-modern flex-1"
                      value={form.whyChooseImage}
                      onChange={(e) => updateField('whyChooseImage', e.target.value)}
                    />
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                      <FaUpload className="w-3.5 h-3.5" /> Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'whyChooseImage')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                <WhyChooseSectionEditor form={form} updateField={updateField} />
              </div>
            </div>

            {/* Tracking section */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Dashboard Tracking Section</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Tracking Section Title</label>
                  <input
                    className="input-modern"
                    value={form.trackTitle}
                    onChange={(e) => updateField('trackTitle', e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">CTA Button Text</label>
                  <input
                    className="input-modern"
                    value={form.trackButtonText}
                    onChange={(e) => updateField('trackButtonText', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Description Body</label>
                <textarea
                  className="input-modern min-h-[70px]"
                  value={form.trackBody}
                  onChange={(e) => updateField('trackBody', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Tracking Section Image</label>
                <div className="flex items-center gap-4">
                  <input
                    className="input-modern flex-1"
                    value={form.trackImage}
                    onChange={(e) => updateField('trackImage', e.target.value)}
                  />
                  <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                    <FaUpload className="w-3.5 h-3.5" /> Upload Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'trackImage')}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Bullet list */}
              <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-dark-700">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Tracking Bullet Points</label>
                  <button
                    onClick={addTrackBullet}
                    className="flex items-center gap-1 text-xs text-primary-600 bg-primary-50 px-2.5 py-1 rounded-lg font-bold"
                  >
                    <FaPlus className="w-2.5 h-2.5" /> Add Bullet
                  </button>
                </div>

                <div className="space-y-2">
                  {form.trackBullets.map((bullet, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <span className="text-sm text-gray-400">#{idx + 1}</span>
                      <input
                        className="input-modern text-sm py-1.5 px-3 flex-1"
                        value={bullet}
                        onChange={(e) => updateTrackBulletField(idx, e.target.value)}
                        placeholder="Monitor progress..."
                      />
                      <button
                        onClick={() => removeTrackBullet(idx)}
                        className="p-2 text-red-500 border rounded-lg hover:bg-red-50"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 4: Formation Guide */}
        {activeTab === 'guide' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Complete Guide Section</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.guideTitle}
                  onChange={(e) => updateField('guideTitle', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Badge Subtitle</label>
                <input
                  className="input-modern"
                  value={form.guideSubtitle}
                  onChange={(e) => updateField('guideSubtitle', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Guide Description Body</label>
              <textarea
                className="input-modern min-h-[70px]"
                value={form.guideBody}
                onChange={(e) => updateField('guideBody', e.target.value)}
              />
            </div>

            {/* Stage/Box metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-b border-gray-100 dark:border-dark-700 py-4">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-gray-400">Step Range / Number (e.g. 1-7)</label>
                <input
                  className="input-modern text-sm"
                  value={form.guideBoxNumber}
                  onChange={(e) => updateField('guideBoxNumber', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-gray-400">Box Header Title</label>
                <input
                  className="input-modern text-sm"
                  value={form.guideBoxTitle}
                  onChange={(e) => updateField('guideBoxTitle', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-gray-400">Box Description Sub-Note</label>
                <input
                  className="input-modern text-sm"
                  value={form.guideBoxSubtitle}
                  onChange={(e) => updateField('guideBoxSubtitle', e.target.value)}
                />
              </div>
            </div>

            {/* List of guide steps */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Step-by-step Procedures</label>
                <button
                  onClick={addGuideStep}
                  className="flex items-center gap-1 text-xs text-primary-600 bg-primary-50 px-2.5 py-1 rounded-lg font-bold"
                >
                  <FaPlus className="w-2.5 h-2.5" /> Add Guide Step
                </button>
              </div>

              <div className="space-y-2">
                {form.guideSteps.map((step, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-sm text-gray-400 font-bold">Step {idx + 1}</span>
                    <input
                      className="input-modern text-sm py-1.5 px-3 flex-1"
                      value={step}
                      onChange={(e) => updateGuideStepField(idx, e.target.value)}
                      placeholder="e.g. Register LLC state filings..."
                    />
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => moveGuideStep(idx, -1)}
                        disabled={idx === 0}
                        className="p-1.5 border rounded-lg hover:bg-gray-100 disabled:opacity-40"
                      >
                        <FaArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveGuideStep(idx, 1)}
                        disabled={idx === form.guideSteps.length - 1}
                        className="p-1.5 border rounded-lg hover:bg-gray-100 disabled:opacity-40"
                      >
                        <FaArrowDown className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeGuideStep(idx)}
                        className="p-1.5 text-red-500 border rounded-lg hover:bg-red-50"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab 5: Page FAQs */}
        {activeTab === 'faqs' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page FAQ Content</h2>
              <button
                onClick={addFAQ}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add FAQ Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Header Title</label>
                <input
                  className="input-modern"
                  value={form.faqsTitle}
                  onChange={(e) => updateField('faqsTitle', e.target.value)}
                  placeholder="USA Company Formation FAQs"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Description</label>
                <input
                  className="input-modern"
                  value={form.faqsSubtitle}
                  onChange={(e) => updateField('faqsSubtitle', e.target.value)}
                  placeholder="Get answers to common questions..."
                />
              </div>
            </div>

            {/* List of FAQs */}
            <div className="space-y-4 pt-4">
              {form.faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeFAQ(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">FAQ Item #{idx + 1}</h3>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Question</label>
                    <input
                      className="input-modern text-sm"
                      value={faq.question}
                      onChange={(e) => updateFAQField(idx, 'question', e.target.value)}
                      placeholder="e.g. Can a non-resident form an LLC?"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Answer</label>
                    <textarea
                      className="input-modern text-sm min-h-[80px]"
                      value={faq.answer}
                      onChange={(e) => updateFAQField(idx, 'answer', e.target.value)}
                      placeholder="Provide answer description text..."
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-premium flex items-center gap-2"
        >
          <FaSave className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </PageLayout>
  );
};

export default USACompanyFormationCMS;
